/** Pure helpers: status -> colours, pill mapping, fleet filter/sort. */

// Badge variants for health statuses. LEGACY is deliberately muted (never teal).
export function healthStatusVariant(status) {
  switch (status) {
    case 'working':   return 'teal';
    case 'recovered': return 'gold';      // amber (never purple)
    case 'problem':   return 'danger';    // red
    case 'offline':   return 'muted';     // slate: distinct from a red problem
    case 'legacy':    return 'muted';
    case 'no_data':
    default:          return 'muted';
  }
}

// 7-day strip cell classes: ok green, recovered amber, problem red,
// offline-all-day dark slate (distinct from problem), unknown = grey OUTLINE.
// No purple anywhere.
export function dayCellClass(status) {
  switch (status) {
    case 'ok':        return 'bg-teal';
    case 'recovered': return 'bg-gold';
    case 'problem':   return 'bg-danger';
    case 'offline':   return 'bg-slate-600';
    case 'unknown':
    default:          return 'bg-transparent border border-white/30';
  }
}

export const DAY_LEGEND = [
  { status: 'ok', label: 'OK' },
  { status: 'recovered', label: 'Recovered' },
  { status: 'problem', label: 'Problem' },
  { status: 'offline', label: 'Offline' },
  { status: 'unknown', label: 'No data' },
];

export function tileStateClass(state) {
  switch (state) {
    case 'ok':         return 'text-teal';
    case 'recovering': return 'text-gold';
    case 'checking':   return 'text-gold';   // build 404: playback not confirmed yet
    case 'problem':    return 'text-danger';
    default:           return 'text-text-muted';
  }
}

export const FLEET_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'problem', label: 'Problem' },
  { key: 'offline', label: 'Offline' },
  { key: 'recovered', label: 'Recovered' },
  { key: 'legacy', label: 'Legacy' },
  { key: 'no_data', label: 'No data' },
];

// Server order is problem->offline->recovered->no_data->legacy; keep it, but a
// stable re-sort makes the guarantee local too (used if the server order slips).
const ORDER = { problem: 0, offline: 1, recovered: 2, no_data: 3, legacy: 4 };
export function sortAttention(rows) {
  return rows
    .map((r, i) => [r, i])
    .sort((a, b) => (ORDER[a[0].status] ?? 9) - (ORDER[b[0].status] ?? 9) || a[1] - b[1])
    .map((x) => x[0]);
}

export function filterAttention(rows, { status = 'all', companyId = '', search = '' } = {}) {
  const q = (search || '').trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (status !== 'all' && r.status !== status) return false;
    if (companyId && String(r.company_id) !== String(companyId)) return false;
    if (q) {
      const hay = `${r.name ?? ''} ${r.company ?? ''} ${r.branch ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function companyOptions(rows) {
  const m = new Map();
  (rows || []).forEach((r) => { if (r.company_id != null) m.set(String(r.company_id), r.company ?? `#${r.company_id}`); });
  return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

// build 414: while the headline describes the LIVE state of the screen, the badge must say the same thing
// (a stale "Recovered since 22:23" next to "Loading..." was two truths on one line).
const LIVE_HEADLINE_BADGE = {
  'headline.loading': ['Checking', 'muted'],
  'headline.media_stalled': ['Repairing', 'gold'],
  'headline.no_sound': ['No sound', 'danger'],
  'headline.external_fault': ['Problem', 'danger'],
  'headline.standby': ['Standby', 'muted'],
  'headline.paused': ['Paused', 'muted'],
  'headline.prayer': ['Prayer', 'muted'],
  'headline.app_closed': ['App closed', 'muted'],
  'headline.scheduled_off': ['Scheduled off', 'muted'],
};
/** @returns {{label:string, variant:string, live:boolean}|null} null = the status badge is the truth */
export function liveHeadlineBadge(headline) {
  const b = headline && LIVE_HEADLINE_BADGE[headline.key];
  return b ? { label: b[0], variant: b[1], live: true } : null;
}
